import type { RouteLocationNormalized, RouteLocationRaw } from "vue-router";
import { applyPreferredColorMode, setColorMode } from "@/shared/composables/useColorMode";
import { useAuthsStore, type RoleSlot } from "@/shared/stores/auth";
import { AUTH_PATH } from "./publicPaths";

function roleHome(role: RoleSlot): string {
  return role === "company" ? "/company" : "/employee";
}

function roleFromPath(value: string): RoleSlot | null {
  const path = value.split(/[?#]/, 1)[0] ?? "";
  if (path === "/company" || path.startsWith("/company/")) return "company";
  if (path === "/employee" || path.startsWith("/employee/")) return "employee";
  return null;
}

function safeRedirect(value: unknown, role: RoleSlot): string | null {
  if (typeof value !== "string" || !value.startsWith("/")) return null;
  const path = value.split(/[?#]/, 1)[0];
  if (value.startsWith("//") || path === AUTH_PATH) return null;
  return roleFromPath(value) === role ? value : null;
}

function validSession(auth: ReturnType<typeof useAuthsStore>, role: RoleSlot): boolean {
  const user = auth.getUserForRole(role);
  return auth.hasSession(role) && user?.role === "user" && user.account_type === role;
}

function loggedInRedirect(to: RouteLocationNormalized): RouteLocationRaw | null {
  const auth = useAuthsStore();
  const requestedRole = roleFromPath(
    typeof to.query.redirect === "string" ? to.query.redirect : "",
  );
  if (requestedRole && validSession(auth, requestedRole)) {
    auth.setActiveRole(requestedRole);
    return safeRedirect(to.query.redirect, requestedRole) ?? roleHome(requestedRole);
  }
  // Do not silently switch to the other slot after this slot's session expires.
  if (requestedRole) return null;
  const active = auth.activeRoleRef;
  if (active && validSession(auth, active)) return roleHome(active);
  const available = (["company", "employee"] as const).find((role) => validSession(auth, role));
  return available ? roleHome(available) : null;
}

export default function handleRole(to: RouteLocationNormalized): RouteLocationRaw | boolean {
  if (to.meta.theme) setColorMode(to.meta.theme, false);
  else applyPreferredColorMode();

  const auth = useAuthsStore();
  if (to.path === AUTH_PATH) {
    // mode=register 仅作为入口意图（如客户端 /register 跳转）：注册对已登录用户同样开放
    if (to.query.mode === "register") return true;
    return loggedInRedirect(to) ?? true;
  }
  if (to.meta.public) return true;

  const role = to.meta.requiredAccountType;
  if (!role) return true;
  if (!validSession(auth, role)) {
    return { path: AUTH_PATH, query: { redirect: to.fullPath } };
  }
  auth.setActiveRole(role);
  return true;
}
